import {
  dineroStockMultiplyCeil,
  magnifyValue,
  toDinero,
} from '@diamond/shared/decimal-util';
import {
  BilTo,
  CancelReason,
  DiscountPromo,
  FreeGoodPromo,
  Order,
  OrderItem,
  OrderMeta,
  Product,
  ProductCommonPrice,
  ProductExcludeBranch,
  ProductExcludeCustomer,
  ProductSpecialPrice,
  ProductStock,
  ProductStockDay,
  ProductValuationType,
  ShipTo,
  User,
} from '@diamond/shared/schema';
import { Static } from '@sinclair/typebox';
import { format } from 'date-fns';

import type {
  Biltos,
  CancelReasons,
  Discount,
  FreeGood,
  HistoryTransaction,
  HistoryTransactionDetail,
  Material,
  MaterialExcludeBranch,
  MaterialExcludeCustomer,
  MIMEType,
  OmitTableDefault,
  PriceListKhusus,
  PriceListUmum,
  SAPActiveStatus,
  SAPDataType,
  SAPDefinition,
  SAPDivision,
  SAPField,
  SAPStorageCond,
  Shiptos,
  Stock,
  StockDay,
  ValuationType,
} from './sap.schema';
import { imageMIME, SAPStatusDescription } from './sap.schema';
import { SAP_FIELD_DEFINITIONS } from './sap.schema';

// Regex validator. compile once to speed up process
const WEBPORTAL_DATE_VALIDATOR = new RegExp(/^\d{4}-\d{2}-\d{2}$/);
const ABAB_DATE_VALIDATOR = new RegExp(/^\d{4}\d{2}\d{2}$/);
const ABAB_TIME_VALIDATOR = new RegExp(/^\d{2}\d{2}\d{2}$/);
const SAP_DATE_VALIDATOR = new RegExp(
  /^([0-9]{2})-(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)-([0-9]{2})$/
);

// RFC SAP Util

function convertValueToNullIfEmpty(field: string): string | null {
  const isFieldEmpty = field?.length === 0 || field === ' ';
  return isFieldEmpty ? null : field;
}
export function defineSAP(field: string): SAPDefinition {
  if (!Object.keys(SAP_FIELD_DEFINITIONS).includes(field))
    throw new Error(`SAP has no field ${field}`);
  return SAP_FIELD_DEFINITIONS[field];
}

function populateOrderType(itemCode: string): string {
  if (itemCode.startsWith('8')) return 'ZE01';

  if (itemCode.startsWith('9')) return 'ZE05';

  return 'ZOR';
}

export function getPlaceholderValue(fieldType: SAPDataType): string {
  switch (fieldType) {
    case 'CHAR':
    case 'CHARBOOL':
    case 'CHARDIVISION':
      return 'X';

    case 'FLOAT':
      return '0.00';

    case 'FLOATZEROPADDED_6':
      return '0';

    case 'DATE':
      return convertDatetoABABDate(new Date());

    case 'INT':
      return '1';

    default:
      return 'X';
  }
}

/**
 *
 * @param ABABDate
 * @param ABABTime
 * @returns JS Date in UTC timezone
 */
export function convertABABDatetoDate(
  ABABDate: string,
  ABABTime?: string
): Date {
  if (!ABAB_DATE_VALIDATOR.test(ABABDate))
    throw new Error(`invalid date input: ${ABABDate}`);

  const month = Number(ABABDate.substring(4, 6));
  const date = Number(ABABDate.substring(6, 8));

  let dateStr = `${ABABDate.substring(0, 4)}-${
    month > 9 ? month : '0' + month
  }-${date > 9 ? date : '0' + date}`;

  if (ABABTime) {
    if (!ABAB_TIME_VALIDATOR.test(ABABTime))
      throw new Error(`invalid time input: ${ABABTime}`);

    const hour = ABABTime.substring(0, 2);
    const minute = ABABTime.substring(2, 4);
    const second = ABABTime.substring(4, 6);

    dateStr = `${dateStr}T${hour}:${minute}:${second}`;
  }

  return new Date(dateStr);
}

export function convertSAPDateToJS(SAPDate: string, SAPTime?: string): Date {
  const isABABDate = ABAB_DATE_VALIDATOR.test(SAPDate);
  const isSAPDate = SAP_DATE_VALIDATOR.test(SAPDate);

  if (!isABABDate && !isSAPDate) {
    throw new Error(`invalid date input: ${SAPTime}`);
  }

  let dateStr = '';
  // YYYYMMDD, format
  if (isABABDate) {
    const year = SAPDate.slice(0, 4);
    const month = SAPDate.slice(4, 6);
    const date = SAPDate.slice(6, 8);

    dateStr = `${year}-${month}-${date}`;
  }

  // DD-MMM-YY, format
  if (isSAPDate) {
    const year = `20${SAPDate.substring(7)}`;
    const monthObj = {
      JAN: '01',
      FEB: '02',
      MAR: '03',
      APR: '04',
      MAY: '05',
      JUN: '06',
      JUL: '07',
      AUG: '08',
      SEP: '09',
      OCT: '10',
      NOV: '11',
      DEC: '12',
    };
    const month = monthObj[`${SAPDate.substring(3, 6)}`];
    const date = SAPDate.substring(0, 2);

    dateStr = `${year}-${month}-${date}`;
  }

  if (SAPTime) {
    if (!ABAB_TIME_VALIDATOR.test(SAPTime))
      throw new Error(`invalid time input: ${SAPTime}`);

    const hour = SAPTime.substring(0, 2);
    const minute = SAPTime.substring(2, 4);
    const second = SAPTime.substring(4, 6);

    dateStr = `${dateStr}T${hour}:${minute}:${second}`;
  }

  return new Date(dateStr);
}

export function convertDatetoABABDate(date: Date) {
  const mm = date.getMonth() + 1;
  const dd = date.getUTCDate();
  return [
    date.getFullYear(),
    mm > 9 ? mm : '0' + mm,
    dd > 9 ? dd : '0' + dd,
  ].join('');
}

export function formatDateInTimeZone(date: Date, timeZone = 'Asia/Jakarta') {
  if (Number.isNaN(date.getTime())) {
    throw new Error(`invalid date input: ${date}`);
  }

  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = dateParts.find((part) => part.type === 'year')?.value;
  const month = dateParts.find((part) => part.type === 'month')?.value;
  const day = dateParts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    throw new Error(`invalid date input: ${date}`);
  }

  return [year, month, day].join('-');
}

export function createStruct(fields: string[]): { [key: string]: string } {
  return fields.reduce((struct, field) => {
    const fieldDef = defineSAP(field);
    const placeholder = getPlaceholderValue(fieldDef.type);

    struct[field] = placeholder;

    return struct;
  }, {});
}

export function addZeroPad(number: number, countDigit: number) {
  return String(number).padStart(countDigit, '0');
}

export function convertDivisionToABAB(
  division: Static<typeof Product>['division']
): 'DIA' | 'TR' | 'FF' | 'EQ' {
  switch (division) {
    case 'diamond':
      return 'DIA';

    case 'trading':
      return 'TR';

    case 'equipment':
      return 'EQ';

    case 'fnb':
      return 'FF';
  }
}

export function autoConvertToJSType<T>(field: string, value: string): T {
  const def = defineSAP(field);
  switch (def.type) {
    case 'DATE': {
      const [date, time] = value.split('_', 2);
      return convertABABDatetoDate(date, time) as T;
    }

    case 'FLOAT':
      return Number(value) as T;

    case 'CHARBOOL':
      return (value === 'X') as T;

    default:
      return value as T;
  }
}

export function autoConvertToABAB<T>(field: SAPField, value: T): string {
  const def = defineSAP(field);
  switch (def.type) {
    case 'DATE':
      return convertDatetoABABDate(value as Date);

    case 'FLOATZEROPADDED_6':
      return addZeroPad(value as number, 6);

    case 'CHARDIVISION':
      return convertDivisionToABAB(value as Static<typeof Product>['division']);

    default:
      return typeof value === 'string' ? value : value.toString();
  }
}

// WebPortal Util

export function convertSAPDivision(
  division: SAPDivision
): Static<typeof Product>['division'] {
  switch (division) {
    case 'EQUIPMENT':
    case 'EQ':
      return 'equipment';

    case 'FINE FOOD':
    case 'FF':
      return 'fnb';

    case 'TRADING':
    case 'TR':
      return 'trading';

    case 'DIA':
      return 'diamond';

    default:
      throw new Error(`error converting ${division}`);
  }
}

export function convertSAPStorageCond(
  storageCond: SAPStorageCond
): Static<typeof Product>['product_storage'] {
  switch (storageCond) {
    case 'CH':
      return 'chilled';

    case 'DA':
      return 'dry_ac';

    case 'DR':
      return 'dry';

    case 'FZ':
      return 'frozen';

    default:
      throw new Error(`error converting ${storageCond}`);
  }
}

export function convertStatusActive(
  activeShipto: SAPActiveStatus,
  blockShipto: SAPActiveStatus
): Static<typeof ShipTo>['status'] {
  if (blockShipto === 'N' && activeShipto === 'Y') return 'active';

  return 'non-active';
}
/**
 *
 * @param date date string from response. format: YYYY-MM-DD
 * @param isStartDate true for start-date, false for end-date
 * @returns Unix timestamps in seconds
 */
export function convertToUnix(
  date: string,
  isStartDate: boolean,
  isUTC: boolean
): number {
  if (!WEBPORTAL_DATE_VALIDATOR.test(date))
    throw new Error(`invalid date string: ${date}`);

  const unixInMilliseconds = new Date(
    `${date}T${isStartDate ? '00:00:00' : '23:59:59'}Z`
  ).getTime();
  if (isUTC) return Math.floor(unixInMilliseconds / 1000);

  // if not UTC, assume from Asia/Jakarta by default, hence subtract 7 hours
  const gmtPlus7Offset = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
  const unixPlus7Milliseconds = unixInMilliseconds - gmtPlus7Offset;

  return Math.floor(unixPlus7Milliseconds / 1000);
}

/**
 *
 * @param date date string from response. format: YYYY-MM-DD
 * @param time time string from response. format: HH:MM:SS
 * @returns Unix timestamps in seconds
 */
export function convertToUnixWithTime(
  date: string,
  time: string,
  isUTC: boolean
): number {
  if (!WEBPORTAL_DATE_VALIDATOR.test(date))
    throw new Error(`invalid date string: ${date}`);

  const unixInMilliseconds = new Date(`${date}T${time}Z`).getTime();
  if (isUTC) return Math.floor(unixInMilliseconds / 1000);

  // if not UTC, assume from Asia/Jakarta by default, hence subtract 7 hours
  const gmtPlus7Offset = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
  const unixPlus7Milliseconds = unixInMilliseconds - gmtPlus7Offset;

  return Math.floor(unixPlus7Milliseconds / 1000);
}

export function convertDateToUnix(date: Date, isUTC: boolean): number {
  const unixInMilliseconds = date.getTime();
  if (isUTC) return Math.floor(unixInMilliseconds / 1000);

  // if not UTC, assume from Asia/Jakarta by default, hence subtract 7 hours
  const gmtPlus7Offset = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
  const unixPlus7Milliseconds = unixInMilliseconds - gmtPlus7Offset;

  return Math.floor(unixPlus7Milliseconds / 1000);
}

/**
 * Converts a string to a number if the string is not empty or undefined.
 * Returns a fallback value if the string is empty or undefined.
 *
 * @param {string | undefined} value - The string to convert to a number.
 * @param {number} [fallback=0] - The fallback value to return if the string is empty or undefined.
 * @returns {number} - The converted number or the fallback value.
 */
const getNumberValue = (value: string | undefined, fallback = 0) =>
  value?.length > 0 ? Number(value) : fallback;

/**
 * Converts a string to a number then magnify with stock magnifier if the string is not empty or undefined.
 * Returns a fallback value if the string is empty or undefined.
 *
 * @param {string | undefined} value - The string to convert to a number.
 * @param {number} [fallback=0] - The fallback value to return if the string is empty or undefined.
 * @returns {number} - The converted number or the fallback value.
 */
function getStockValue(value: string | undefined, fallback = 0): number {
  const numValue = value?.length > 0 ? Number(value) : fallback;
  if (numValue === 0) return 0;

  const { magnifiedValue } = magnifyValue(numValue, 'stock');
  return magnifiedValue;
}

/**
 * Returns the string if it is not empty or undefined.
 * Returns null if the string is empty or undefined.
 *
 * @param {string | undefined} value - The string to check.
 * @returns {string | null} - The original string or null.
 */
function getStringValue(value: string | undefined, fallback = null) {
  if (!value) return fallback;
  return value.trim().length === 0 ? fallback : value;
}

function toNullableNumber(value: unknown): number | null {
  if (value === "" || value === undefined || value === null) return null;
  return Number(value);
}

// Material
export function convertMaterialToProductSchema(
  material: Material
): OmitTableDefault<Static<typeof Product>> & {
  category: string;
  sub_category: string;
  third_sub_category: string;
} {
  const minimumOrderQtyRaw =
    material.MINIMUM_ORDER_QTY === '' // If MINIMUM_ORDER_QUANTITY is an empty string,
      ? 1 // it means that the miminum order quantity is 1.
      : Number(material.MINIMUM_ORDER_QTY); // Else, convert MINIMUM_ORDER_QUANTITY string to number
  const minimumOrderQty = magnifyValue(minimumOrderQtyRaw, 'stock');

  const orderMultipleQtyRaw =
    material.CASE_QTY === '' ? 1 : Number(material.CASE_QTY);
  const orderMultipleQty = magnifyValue(orderMultipleQtyRaw, 'stock');
  const conversionQTYRaw =
    material.CONVERSION_QTY !== '' ? Number(material.CONVERSION_QTY) : 1;
  const conversionQTY = magnifyValue(conversionQTYRaw, 'stock');
  return {
    item_code: material.MATERIAL,
    title: material.MATERIAL_DESCRIPTION,
    desc: material.MATERIAL_LONG_TEXT,
    brand_name: material.MATERIAL_GROUP,
    brand_desc: material.MATERIAL_GROUP_DESC,
    pack_unit: material.PACK_SIZE_SALES_UOM,
    measurement_unit: material.BASE_UNIT_MEASURE,
    measurement_unit_desc: material.BASE_UNIT_MEASURE_DESC,
    sales_unit: material.SALES_UNIT,
    sales_unit_desc: material.SALES_UNIT_DESC,
    type: material.MATERIAL_TYPE,
    dimension: material.SIZE_DIMENSION,
    dimension_unit: material.UNIT_DIMENSION,
    gross_weight: Number(material.GROSS_WEIGHT),
    net_weight: Number(material.NET_WEIGHT),
    weight_unit: material.WEIGHT_UNIT,
    gross_weight_desc: material.GROSS_WEIGHT_DESC,
    net_weight_desc: material.NET_WEIGHT_DESC,
    volume: Number(material.VOLUME),
    volume_unit: material.VOLUME_UNIT,
    product_storage: convertSAPStorageCond(material.STORAGE_COND),
    transportation_group: material.TRANSPORTATION_GROUP,
    barcode: material.EAN_UPC,
    category: material.PRODUCT_HIERARCHY_LVL1,
    category_id: material.PRODUCT_HIERARCHY_LVL1_CODE,
    sub_category: material.PRODUCT_HIERARCHY_LVL2,
    sub_category_id: material.PRODUCT_HIERARCHY_LVL2_CODE,
    third_sub_category: material.PRODUCT_HIERARCHY_LVL3,
    third_sub_category_id: material.PRODUCT_HIERARCHY_LVL3_CODE,
    material_division: convertSAPDivision(material.DIVISION),
    can_overwrite_price: material.FIX_PRICE !== 'X',
    category_2: material.CATEGORY_2,
    division:
      material.FLAG_SHOW_PEPPERI === 'X'
        ? convertSAPDivision(material.MKT_CAT_3)
        : convertSAPDivision('DIA'),
    tag: material.TAG_ITEM,
    information: material.PRODUCT_INFORMATION,
    composition: material.KOMPOSISI,
    superiority: material.PRODUCT_EXCELLENCE,
    flag_fs: material.FLAG_FS ? material.FLAG_FS === 'X' : false,
    flag_gt: material.FLAG_GT ? material.FLAG_FS === 'X' : false,
    flag_sts: material.FLAG_FS,
    flag_show_pepperi: material.FLAG_SHOW_PEPPERI
      ? material.FLAG_SHOW_PEPPERI === 'X'
      : false,
    is_valid_data: material.IS_VALID_DATA
      ? material.IS_VALID_DATA === 'X'
      : false,
    finance_category_5: material.FI_CATEGORY_5,
    finance_category_6: material.FI_CATEGORY_6,
    finance_category_7: material.FI_CATEGORY_7,
    suggestion_1: material.SUGGESTION_1,
    suggestion_2: material.SUGGESTION_2,
    minimum_order_quantity: minimumOrderQty.magnifiedValue,
    order_quantity_multiple: orderMultipleQty.magnifiedValue,
    is_only_for_sukanda_partner: material.FLAG_MITRA_SUKANDA === 'X',
    is_only_for_online_business: material.FLAG_ONLINE_BISNIS === 'X',
    is_eligible_for_so_automation: material.FLAG_AUTOMATED_SO === 'X',
    ...(material.FLAG_AUTOMATED_DO 
      ? {
          is_eligible_for_do_automation: material.FLAG_AUTOMATED_DO === 'X',
        }
      : {}),
    conversion_qty: conversionQTY.magnifiedValue,
    conversion_sales_unit: material.CONVERSION_SALES_UNIT,
    conversion_unit: material.CONVERSION_UNIT,
    min_remaining_shelf_life: toNullableNumber(material.MIN_REMAINING_SHELF_LIFE),
    total_shelf_life: toNullableNumber(material.TOTAL_SHELF_LIFE),
    ind_shelf_life_exp_date: material.IND_SHELF_LIFE_EXP_DATE ?? null,
    product_group: material.PRODUCT_GROUP ?? null,
  };
}

export function convertPLUToCommonPriceSchema(
  plu: PriceListUmum
): OmitTableDefault<Static<typeof ProductCommonPrice>> {
  return {
    price_id: plu.PRICE_ID,
    product_item_code: plu.MATERIAL,
    distribution_channel: plu.DISTRIBUTION_CHANNEL,
    cg_1_business_segmentation: plu.COND_GROUP_1,
    cg_4_manufacture: plu.COND_GROUP_4,
    cg_5_trading: plu.COND_GROUP_5,
    valuation_type: plu.VALUATION_TYPE,
    price: Math.ceil(plu.PRICE_NUMBER),
    valid_from: convertToUnix(plu.VALID_FROM, true, false),
    valid_until: convertToUnix(plu.VALID_UNTIL, false, false),
    sap_updated_at: convertToUnixWithTime(
      plu.DATE_UPDATED,
      plu.TIME_UPDATED,
      false
    ),
  };
}

export function convertPLKToSpecialPriceSchema(
  plk: PriceListKhusus
): OmitTableDefault<Static<typeof ProductSpecialPrice>> {
  return {
    price_id: plk.PRICE_ID,
    product_item_code: plk.MATERIAL,
    sap_no_shipto: plk.CUSTOMER,
    distribution_channel: plk.DISTRIBUTION_CHANNEL,
    valuation_type: plk.VALUATION_TYPE,
    price: Math.ceil(plk.PRICE_NUMBER),
    valid_from: convertToUnix(plk.VALID_FROM, true, false),
    valid_until: convertToUnix(plk.VALID_UNTIL, false, false),
    sap_updated_at: convertToUnixWithTime(
      plk.DATE_UPDATED,
      plk.TIME_UPDATED,
      false
    ),
  };
}

export function convertStockToProductStockSchema(
  plu: Stock
): OmitTableDefault<Static<typeof ProductStock>> {
  const { magnifiedValue } = magnifyValue(plu.STOCK, 'stock');

  return {
    product_item_code: plu.MATERIAL,
    distribution_channel: plu.DISTRIBUTION_CHANNEL,
    plant: plu.PLANT,
    storage_location: plu.STORAGE_LOCATION,
    stock: magnifiedValue,
  };
}

export function convertStockToProductStockDaySchema(
  plu: StockDay
): OmitTableDefault<Static<typeof ProductStockDay>> {
  return {
    product_item_code: plu.MATERIAL,
    distribution_channel: plu.DISTRIBUTION_CHANNEL,
    plant: plu.PLANT,
    storage_location: plu.STORAGE_LOCATION,
    stock_days: plu.STOCK_DAYS,
  };
}

export function convertAccountToBiltosSchema(
  bilto: Biltos
): OmitTableDefault<Static<typeof BilTo>> {
  return {
    active_bilto: bilto.ACTIVE_BILTO,
    address1_bilto: bilto.ADDRESS1_BILTO,
    atribut1_bilto: bilto.ATRIBUT1_BILTO,
    atribut2_bilto: bilto.ATRIBUT2_BILTO,
    block_bilto: bilto.BLOCK_BILTO,
    city_bilto: bilto.CITY_BILTO,
    company_name_bilto: bilto.COMPANY_NAME_BILTO,
    cpy_title_bilto: bilto.CPY_TITLE_BILTO,
    created_date: bilto.CREATED_DATE,
    creator_dist_ch: bilto.CREATOR_DIST_CH,
    crm_limit_bilto: bilto.CR_LIMIT_BILTO,
    dist_ch_bilto: bilto.DIST_CH_BILTO,
    div_bilto: bilto.DIV_BILTO,
    dw_status_bilto: bilto.DW_STS_BILTO,
    fax_bilto: bilto.FAX_BILTO,
    first_name_bilto: bilto.FIRST_NAME_BILTO,
    flag_col_submit: bilto.FLAG_COLL_SUBMIT,
    flag_distr_submit: bilto.FLAG_DISTR_SUBMIT,
    hp_bilto: bilto.HP_BILTO,
    hp_bilto2: bilto.HP_BILTO2,
    hp_bilto3: bilto.HP_BILTO3,
    id_crlim_cr_dependency: bilto.ID_CRLIM_CR_DEPEDENCY,
    id_kil_bilto: bilto.ID_KIL_BILTO,
    id_no_bilto: bilto.ID_NO_BILTO,
    lang_bilto: bilto.LANG_BILTO,
    last_name_bilto: bilto.LAST_NAME_BILTO,
    lm_address1_bilto: bilto.LM_ADDRESS1_BILTO,
    mstr_appr_sts_bilto: bilto.MSTR_APPR_STS_BILTO,
    orac_no_bilto: bilto.ORAC_NO_BILTO,
    reas_bilto: bilto.REAS_BILTO,
    rej_by_bilto: bilto.REJ_BY_BILTO,
    req_sts_bilto: bilto.REQ_STS_BILTO,
    sales_district_bilto: bilto.SALES_DISTRICT,
    sales_org_bilto: bilto.SALES_ORG_BILTO,
    sap_no_bilto: +bilto.SAP_NO_BILTO,
    sc_company_name_bilto: bilto.SC_COMPANY_NAME_BILTO,
    search_term_bilto_2: bilto.SEARCH_TERM_BILTO_2,
    tax_bilto: bilto.TAX_BILTO,
    telp_bilto: bilto.TELP_BILTO,
    telp_bilto2: bilto.TELP_BILTO2,
    telp_bilto3: bilto.TELP_BILTO3,
    timezone_bilto: bilto.TIME_ZONE_BILTO,
    tipe_id: bilto.TIPE_ID,
    top_bilto: bilto.TOP_BILTO,
    type_bilto: bilto.TYPE_BILTO,
    vat_bilto: bilto.VAT_BILTO,
    zip_code_bilto: bilto.ZIP_CODE_BILTO,
    email_sol: bilto.EMAIL_SOL.toLowerCase(),
    first_name_sol: bilto.FIRST_NAME_SOL,
    flag_sol: bilto.FLAG_SOL,
    last_name_sol: bilto.LAST_NAME_SOL,
    telp_sol: bilto.TELP_SOL,
    top_desc: bilto.TOP_DESC,
    mid_name_bilto: bilto.MID_NAME_BILTO,
    email_bilto: bilto.EMAIL_BILTO,
    dept_bilto: bilto.DEPT_BILTO,
    function_bilto: bilto.FUNCTION_BILTO,
    address2_bilto: bilto.ADDRESS2_BILTO,
    prov_bilto: bilto.PROV_BILTO,
    district_bilto: bilto.DISTRICT_BILTO,
    village_bilto: bilto.VILLAGE_BILTO,
    freezer_bilto: bilto.FREEZER_BILTO,
    payment_method_bilto: bilto.PAYMENT_METHOD_BILTO,
    acc_no_bilto: bilto.ACC_NO_BILTO,
    acc_name_bilto: bilto.ACC_NAME_BILTO,
    bank_name_bilto: bilto.BANK_NAME_BILTO,
    acc_branch_bilto: bilto.ACC_BRANCH_BILTO,
    bank_key_bilto: bilto.BANK_KEY_BILTO,
    mstr_appr_date_bilto: bilto.MSTR_APPR_DATE_BILTO,
    mstr_appr_by_bilto: bilto.MSTR_APPR_BY_BILTO,
    final_appr_time_bilto: bilto.FINAL_APPR_TIME_BILTO,
    acct_appr_time_bilto: bilto.ACCT_APPR_TIME_BILTO,
    mstr_appr_time_bilto: bilto.MSTR_APPR_TIME_BILTO,
    created_time: bilto.CREATED_TIME,
    atribut3_bilto: bilto.ATRIBUT3_BILTO,
    no_spkp: bilto.NO_SPKP,
    final_appr_sts_bilto: bilto.FINAL_APPR_STS_BILTO,
    final_appr_date_bilto: bilto.FINAL_APPR_DATE_BILTO,
    final_appr_by_bilto: bilto.FINAL_APPR_BY_BILTO,
    acct_appr_sts_bilto: bilto.ACCT_APPR_STS_BILTO,
    acct_appr_date_bilto: bilto.ACCT_APPR_DATE_BILTO,
    acct_appr_by_bilto: bilto.ACCT_APPR_BY_BILTO,
    final_appr_sts_olb: bilto.FINAL_APPR_STS_OLB,
    final_appr_by_olb: bilto.FINAL_APPR_BY_OLB,
    final_appr_time_olb: bilto.FINAL_APPR_TIME_OLB,
    stop_warn: bilto.STOP_WARN,
    sfa: bilto.SFA,
    final_appr_date_olb: bilto.FINAL_APPR_DATE_OLB,
    created_sol: bilto.CREATED_SOL,
    last_update: bilto.LAST_UPDATE,
    flag_dummy: bilto.FLAG_DUMMY,
  };
}

export function convertPVTToProductValuationTypeSchema(
  pvt: ValuationType
): OmitTableDefault<Static<typeof ProductValuationType>> {
  return {
    product_item_code: pvt.MATERIAL,
    valuation_type: pvt.VALUATION_TYPE,
  };
}

export function convertShiptoToSchema(
  shipto: Shiptos
): OmitTableDefault<Static<typeof ShipTo>> {
  return {
    adddres2_shipto: shipto.ADDRESS2_SHIPTO,
    address: shipto.ADDRESS1_SHIPTO,
    attribut2_shipto: shipto.ATRIBUT2_SHIPTO,
    billing_address: shipto.BILLING_ADDRESS,
    block_shipto: shipto.BLOCK_SHIPTO,
    cg_1_business_segmentation: shipto.COND_GROUP_1_SHIPTO,
    cg_2_business_category: shipto.COND_GROUP_2_SHIPTO,
    cg_3_business_type: shipto.COND_GROUP_3_SHIPTO,
    cg_4_manufacture: shipto.COND_GROUP_4,
    cg_5_trading: shipto.COND_GROUP_5,
    cg_1_business_segmentation_desc: shipto.DESC_COND_1,
    cg_2_business_category_desc: shipto.DESC_COND_2,
    cg_3_business_type_desc: shipto.DESC_COND_3,
    cg_4_manufacture_desc: shipto.DESC_COND_4,
    cg_5_trading_desc: shipto.DESC_COND_5,
    changed_type_shipto: shipto.CHANGED_TYPE_SHIPTO,
    city: shipto.CITY_SHIPTO,
    code: shipto.ID_KIL_SHIPTO,
    col_bilto: shipto.COL_SHIPTO,
    colector_shipto: shipto.COLLECTOR_SHIPTO,
    coll_day_shipto01: shipto.COLL_DAY_SHIPTO1,
    coll_day_shipto02: shipto.COLL_DAY_SHIPTO2,
    coll_day_shipto03: shipto.COLL_DAY_SHIPTO3,
    coll_day_shipto05: shipto.COLL_DAY_SHIPTO5,
    coll_day_shipto06: shipto.COLL_DAY_SHIPTO6,
    country: convertValueToNullIfEmpty(shipto.COUNTRY_SHIPTO),
    cpy_title_shipto: shipto.CPY_TITLE_SHIPTO,
    created_date: shipto.CREATED_DATE,
    created_date_time: shipto.CREATED_DATE_TIME,
    creator_dist_ch: shipto.CREATOR_DIST_CH,
    deliv_date_shipto: shipto.DELIV_DATE_SHIPTO,
    deliv_plant_shipto: shipto.DELIV_PLANT_SHIPTO,
    distribution_channel: shipto.DIST_CH_SHIPTO,
    distribution_channel_desc: shipto.DESC_DIST_CH,
    district_shipto: shipto.DISTRICT_SHIPTO,
    div_shipto: shipto.DIV_SHIPTO,
    dw_sts_shipto: shipto.DW_STS_SHIPTO,
    email_shipto: shipto.EMAIL_SHIPTO,
    first_name_shipto: shipto.FIRST_NAME_SHIPTO,
    fiscal_address: shipto.FISCAL_ADDRESS,
    flag_show_pepperi: shipto.FLAG_SHOW_PEPPERI,
    coll_day_shipto04: shipto.COLL_DAY_SHIPTO4,
    freezer_bilto: shipto.FREEZER_BILTO,
    hp_pic_col_shipto: shipto.HP_PIC_COL_SHIPTO,
    hp_shipto: shipto.HP_SHIPTO,
    hp_shipto2: shipto.HP_SHIPTO2,
    hp_shipto3: shipto.HP_SHIPTO3,
    id_kil_bilto: shipto.ID_KIL_BILTO,
    id_kil_shipto: shipto.ID_KIL_SHIPTO,
    id_sales_fs_dia: shipto.ID_SALES_FS_DIA,
    id_sales_fs_trd: shipto.ID_SALES_FS_TRD,
    id_spv_sales_fs_dia: shipto.ID_SPV_SALES_FS_DIA,
    id_spv_sales_fs_trd: shipto.ID_SPV_SALES_FS_TRD,
    kae_code_dia_shipto: shipto.KAE_CODE_DIA_SHIPTO,
    kae_code_trd_shipto: shipto.KAE_CODE_TRD_SHIPTO,
    kam_code_dia_shipto: shipto.KAM_CODE_DIA_SHIPTO,
    kam_code_trd_shipto: shipto.KAM_CODE_TRD_SHIPTO,
    kode_pricelist_fs_dia: shipto.KODE_PRICELIST_FS_DIA,
    kode_pricelist_fs_trd: shipto.KODE_PRICELIST_FS_TRD,
    kode_sales_fs_dia: shipto.KODE_SALES_FS_DIA,
    kode_sales_fs_trd: shipto.KODE_SALES_FS_TRD,
    kode_spv_sales_fs_dia: shipto.KODE_SPV_SALES_FS_DIA,
    kode_spv_sales_fs_trd: shipto.KODE_SPV_SALES_FS_TRD,
    last_name_shipto: shipto.LAST_NAME_SHIPTO,
    lat_shipto: shipto.LAT_SHIPTO,
    lm_addrees1_shipto: shipto.LM_ADDRESS1_SHIPTO,
    long_shipto: shipto.LONG_SHIPTO,
    message: shipto.MESSAGE,
    mstr_appr_sts_shipto: shipto.MSTR_APPR_STS_SHIPTO,
    name: shipto.COMPANY_NAME_SHIPTO,
    orac_no_shipto: shipto.ORAC_NO_SHIPTO,
    pic_name: shipto.PIC_COL_SHIPTO,
    pod_bilto: shipto.POD_SHIPTO,
    postal_code: shipto.ZIP_CODE_SHIPTO,
    priority_shipto: shipto.PRIORITY_SHIPTO,
    province: convertValueToNullIfEmpty(shipto.PROV_SHIPTO),
    reas_shipto: shipto.REAS_SHIPTO,
    rej_by_shipto: shipto.REJ_BY_SHIPTO,
    req_sts_shipto: shipto.REQ_STS_SHIPTO,
    sales_district_shipto: shipto.SALES_DISTRICT_SHIPTO,
    sales_id_shipto: shipto.SALES_ID_SHIPTO_TRD,
    sales_org_shipto: shipto.SALES_ORG_SHIPTO,
    salesman_shipto_trd: shipto.SALESMAN_SHIPTO_TRD,
    same_as_bilto: shipto.SAME_AS_BILTO,
    sap_no_shipto: +shipto.SAP_NO_SHIPTO,
    sc_company_name_shipto: shipto.SC_COMPANY_NAME_SHIPTO,
    search_term_shipto: shipto.SEARCH_TERM_SHIPTO,
    search_term_shipto_2: shipto.SEARCH_TERM_SHIPTO_2,
    shipto_type: shipto.SHIPTO_TYPE,
    sof_shipto: shipto.SOF_SHIPTO,
    spv_sales_id_shipto: shipto.SPV_SALES_ID_SHIPTO_TRD,
    spv_salesman_shipto: shipto.SPV_SALESMAN_SHIPTO_TRD,
    status: convertStatusActive(shipto.ACTIVE_SHIPTO, shipto.BLOCK_SHIPTO),
    stop_warn_credit_limit: shipto.STOP_WARN_CREDIT_LIMIT,
    telp_shipto: shipto.TELP_SHIPTO,
    telp_shipto2: shipto.TELP_SHIPTO2,
    telp_shipto3: shipto.TELP_SHIPTO3,
    tfiscal_adddress: shipto.TFISCAL_ADDRESS,
    time_zone_shipto: shipto.TIME_ZONE_SHIPTO,
    tranz_zone_shipto: shipto.TRANZ_ZONE_SHIPTO,
    village_shipto: shipto.VILLAGE_SHIPTO,
    external_id: null,
    flag_dummy: shipto.FLAG_DUMMY,
    attribut9_shipto: shipto.ATRIBUT9_SHIPTO,
    grouping_shipto: shipto.GROUPING_SHIPTO, // attribut 8
    tax_shipto:  shipto.TAX_SHIPTO,
  };
}

export function getMimeType(format: string): MIMEType {
  const stdFormat = format.toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(imageMIME, stdFormat))
    throw new Error(`invalid format ${format}`);

  return imageMIME[stdFormat];
}

export function convertCancelReasonToCancelReasonSchema(
  cr: CancelReasons
): OmitTableDefault<Static<typeof CancelReason>> {
  return {
    reason_id: cr.REASON_ID,
    reason: cr.REASON,
    reason_type: cr.REASON_TYPE,
    reason_status: cr.REASON_STATUS,
  };
}

export function convertMaterialExcludeBranchToProductExcludeBranchSchema(
  meb: MaterialExcludeBranch
): OmitTableDefault<Static<typeof ProductExcludeBranch>> {
  return {
    product_item_code: meb.MATERIAL,
    plant: meb.PLANT,
    distribution_channel: meb.DISTRIBUTION_CHANNEL,
    cg_1_business_segmentation: (meb.COND_GROUP_1_SHIPTO ?? '').trim(),
  };
}

export function convertAccountShiptoToAccountShiptoSchema(
  accountShipto: Biltos & { shipto?: Shiptos[] }
): OmitTableDefault<Static<typeof BilTo>> & {
  shipto?: OmitTableDefault<Static<typeof ShipTo>>[];
} {
  return {
    ...convertAccountToBiltosSchema(accountShipto),
    shipto: (accountShipto.shipto || []).map((account) => {
      return { ...convertShiptoToSchema(account) };
    }),
  };
}

export function convertMaterialExcludeCustomerToProductExcludeCustomerSchema(
  mec: MaterialExcludeCustomer
): OmitTableDefault<Static<typeof ProductExcludeCustomer>> {
  return {
    product_item_code: mec.MATERIAL,
    sap_no_shipto: mec.SHIPTO,
  };
}

export function convertDiscountToDiscountPromoSchema(
  discount: Discount
): OmitTableDefault<Static<typeof DiscountPromo>> {
  const isDiscount = discount.KONWA === '%';
  let deductionAmount = Number(discount.NETWR.replace('-', ''));
  if (isDiscount)
    deductionAmount = magnifyValue(
      deductionAmount,
      'percentage'
    ).magnifiedValue;

  const prorate = discount.PRORATE
    ? discount.PRORATE === 'X'
    : discount.PRORT === 'X';

  return {
    order_type: getStringValue(discount.AUART, ''), // unique key, set '' for empty value
    sap_no_shipto: getNumberValue(discount.KUNNR, 0), // unique key, set 0 for empty value
    sales_org_shipto: getStringValue(discount.VKORG, ''), // unique key, set '' for empty value
    distribution_channel: getStringValue(discount.VTWEG, ''), // unique key, set '' for empty value
    cg_1_business_segmentation: getStringValue(discount.KDKG1, ''), // unique key, set '' for empty value
    cg_2_business_category: getStringValue(discount.KDKG2, ''), // unique key, set '' for empty value
    cg_3_business_type: getStringValue(discount.KDKG3, ''), // unique key, set '' for empty value
    cg_4_manufacture: getStringValue(discount.KDKG4, ''), // unique key, set '' for empty value
    attribute_1: getStringValue(discount.KATR1, ''), // unique key, set '' for empty value
    attribute_2: getStringValue(discount.KATR2, ''), // unique key, set '' for empty value
    attribute_8: getStringValue(discount.KATR8, ''), // unique key, set '' for empty value
    attribute_9: getStringValue(discount.KATR9, ''), // unique key, set '' for empty value
    product_brand_name: getStringValue(discount.MATKL, ''), // unique key, set '' for empty value
    product_type: getStringValue(discount.MTART, ''), // unique key, set '' for empty value
    product_item_code: getNumberValue(discount.MATNR, 0), // unique key, set 0 for empty value
    minimum_order_quantity: getStockValue(discount.MINQTY),
    minimum_order_amount: getNumberValue(discount.MINAMT),
    sum_minimum_order_quantity: getStockValue(discount.SUMMINQTY),
    sum_minimum_order_amount: getNumberValue(discount.SUMMINAMT),
    condition_type: getStringValue(discount.KSCHL, ''), // unique key, set '' for empty value
    calculation_number: getNumberValue(discount.ZCALCNUM, 1), // unique key, set 1 for empty value
    deduction_amount: deductionAmount,
    is_discount: isDiscount,
    condition_pricing_unit: getStringValue(discount.KPEIN),
    condition_unit: getStringValue(discount.KMEIN),
    valid_from: convertDateToUnix(
      convertSAPDateToJS(discount.VALFR, '000000'),
      false
    ),
    valid_until: convertDateToUnix(
      convertSAPDateToJS(discount.VALTO, '155959'),
      false
    ),
    sap_created_at: format(convertSAPDateToJS(discount.ERDAT), 'yyyy-MM-dd'),
    prorate: prorate,
    promo_code: discount.KNUMH, // unique key, always present
    is_from_sap: false,
  };
}

export function convertFreeGoodToFreeGoodPromoSchema(
  freeGood: FreeGood
): OmitTableDefault<Static<typeof FreeGoodPromo>> {
  const minOrderQuantity =
    getStockValue(freeGood.MINQTY) || getStockValue(freeGood.KNRMM);
  const minOrderAmount =
    getNumberValue(freeGood.MINAMT) || getNumberValue(freeGood.MINAMO);
  const sumMinOrderAmount =
    getNumberValue(freeGood.SUMMINAMT) || getNumberValue(freeGood.SUMMINAMO);

  return {
    order_type: getStringValue(freeGood?.AUART || freeGood?.['AUART '], ''), // unique key, set '' for empty value
    sap_no_shipto: getNumberValue(freeGood.KUNNR, 0), // unique key, set 0 for empty value
    sales_org_shipto: getStringValue(freeGood.VKORG, ''), // unique key, set '' for empty value
    distribution_channel: getStringValue(freeGood.VTWEG, ''), // unique key, set '' for empty value
    cg_1_business_segmentation: getStringValue(freeGood.KDKG1, ''), // unique key, set '' for empty value
    cg_2_business_category: getStringValue(freeGood.KDKG2, ''), // unique key, set '' for empty value
    cg_3_business_type: getStringValue(freeGood.KDKG3, ''), // unique key, set '' for empty value
    attribute_6: getStringValue(freeGood.KATR6, ''), // unique key, set '' for empty value
    attribute_8: getStringValue(freeGood.KATR8, ''), // unique key, set '' for empty value
    attribute_9: getStringValue(freeGood.KATR9, ''), // unique key, set '' for empty value
    product_brand_name: getStringValue(freeGood.MATKL, ''), // unique key, set '' for empty value
    product_type: getStringValue(freeGood.MTART, ''), // unique key, set '' for empty value
    product_item_code: getNumberValue(freeGood.MATNR, 0), // unique key, set 0 for empty value
    condition_type: getStringValue(freeGood.KSCHL, ''), // unique key, set '' for empty value
    sequential_number_for_free_goods_scale: getNumberValue(freeGood.KNRPOS, 1), // unique key, set 1 for empty value
    uom_base_material: getStringValue(freeGood.KNRME),
    uom_free_goods: getStringValue(freeGood.KNREZ),
    quantity_free_goods: getStockValue(freeGood.KNRZM, 0),
    minimum_order_quantity: minOrderQuantity,
    minimum_order_amount: minOrderAmount,
    sum_minimum_order_quantity: getStockValue(freeGood.SUMMINQTY, 0),
    sum_minimum_order_amount: sumMinOrderAmount,
    free_goods_product_item_code: getNumberValue(freeGood.KNRMAT, null),
    free_goods_category: freeGood.KNRDD === '1' ? 'inclusive' : 'exclusive',
    valid_from: convertDateToUnix(
      convertSAPDateToJS(freeGood.VALFR, '000000'),
      false
    ),
    valid_until: convertDateToUnix(
      convertSAPDateToJS(freeGood.VALTO, '155959'),
      false
    ),
    sap_created_at: format(convertSAPDateToJS(freeGood.ERDAT), 'yyyy-MM-dd'),
    promo_code: freeGood.KNUMH, // unique key, always present
    is_from_sap: false,
  };
}

// -----------------------
// Transaction -> Order
// -----------------------
export function getSapStatusDescription(
  sapStatusDescription: SAPStatusDescription
): OmitTableDefault<Static<typeof Order>>['status'] {
  let status: OmitTableDefault<Static<typeof Order>>['status'] = 'received';
  if (sapStatusDescription === SAPStatusDescription.CANCELED) {
    status = 'cancelled';
  }

  return status;
}

export function getUserPaymentMethodFromSap(
  sapPaymentMethodDescription: string
): {
  payment_method: OmitTableDefault<Static<typeof User>>['payment_method'];
  term_of_payment_days?: OmitTableDefault<
    Static<typeof User>
  >['term_of_payment_days'];
} {
  let paymentMethod: OmitTableDefault<Static<typeof User>>['payment_method'] =
    'cash';
  let term_of_payment_days = null;

  if (sapPaymentMethodDescription.includes('HARI')) {
    paymentMethod = 'term_of_payment';
    term_of_payment_days = Number(sapPaymentMethodDescription.split(' ')[0]);
  }
  return {
    payment_method: paymentMethod,
    term_of_payment_days,
  };
}

export function getOrderPaymentMethodFromSap(
  sapPaymentMethodDescription: string
): {
  payment_method: OmitTableDefault<Static<typeof OrderMeta>>['payment_method'];
  term_of_payment_days?: OmitTableDefault<
    Static<typeof OrderMeta>
  >['term_of_payment_days'];
} {
  let paymentMethod: OmitTableDefault<
    Static<typeof OrderMeta>
  >['payment_method'] = 'bank_transfer';

  let term_of_payment_days = null;

  if (sapPaymentMethodDescription === 'Tunai') {
    paymentMethod = 'cash_on_delivery';
  }
  if (sapPaymentMethodDescription.includes('HARI')) {
    paymentMethod = 'term_of_payment';
    term_of_payment_days = Number(sapPaymentMethodDescription.split(' ')[0]);
  }
  return {
    payment_method: paymentMethod,
    term_of_payment_days,
  };
}

export function convertHistoryTransactionToOrderMetaSchema(
  transaction: HistoryTransaction,
  rest: {
    user: OmitTableDefault<Static<typeof OrderMeta>>['user'];
    ship_to: OmitTableDefault<Static<typeof OrderMeta>>['ship_to'];
    bill_to: OmitTableDefault<Static<typeof OrderMeta>>['bill_to'];
  }
): OmitTableDefault<Static<(typeof OrderMeta)['insert']>> {
  const status = getSapStatusDescription(transaction.STATUSDESCRIPTION);
  const { payment_method, term_of_payment_days } = getOrderPaymentMethodFromSap(
    transaction.TERM_OF_PAYMENT_DESC
  );
  const processedDate =
    transaction.DATE_SAP?.length && transaction.TIME_SAP?.length
      ? convertSAPDateToJS(transaction.DATE_SAP, transaction.TIME_SAP)
      : null;

  return {
    total_items: transaction.DETAIL.length,
    total: magnifyValue(Number(transaction.GRAND_TOTAL), 'stock')
      .magnifiedValue,
    overall_order_item_stock_status: 'on_stock',
    payment_method,
    term_of_payment_days,
    is_submitted: status !== 'cancelled',
    transaction_id: transaction.TRANSACTION_ID,
    user: rest.user,
    created_at: processedDate,
    updated_at: processedDate,
    is_dummy: false,
    shipto_id: rest.ship_to.id,
    ...rest,
  };
}

const uriValidator = (input: string): boolean => input.startsWith('http');

export function convertHistoryTransactionToOrderSchema(
  transaction: HistoryTransaction,
  rest: {
    division: OmitTableDefault<Static<typeof Order>>['division'];
    order_meta_id: string;
    is_out_of_delivery: boolean;
  }
): OmitTableDefault<Static<(typeof Order)['insert']>> {
  const invoiceLinkRaw = transaction.INVOICELINK.replace(' ', '');
  const invoiceLink = uriValidator(invoiceLinkRaw) ? invoiceLinkRaw : null;

  const deliveryLinkRaw = transaction.DELIVERYLINK.replace(' ', '');
  const deliveryLink = uriValidator(deliveryLinkRaw) ? deliveryLinkRaw : null;
  const processedDate =
    transaction.DATE_SAP?.length && transaction.TIME_SAP?.length
      ? convertSAPDateToJS(transaction.DATE_SAP, transaction.TIME_SAP)
      : null;

  const subtotal = Number(transaction.GRAND_TOTAL);

  return {
    total_items: transaction.DETAIL.length,
    original_subtotal: subtotal,
    subtotal,
    invoice_link: invoiceLink,
    delivery_link: deliveryLink,
    sub_po_number: transaction.ORDER_ID,
    so_number: transaction.SO_NUMBER,
    status: getSapStatusDescription(transaction.STATUSDESCRIPTION),
    division: rest.division,
    order_meta_id: rest.order_meta_id,
    created_at: processedDate,
    updated_at: processedDate,
    processed_at: processedDate,
  };
}

export function convertHistoryTransactionDetailToOrderItemSchema(
  detail: HistoryTransactionDetail,
  rest: {
    order_id: string;
    product_id: string;
    product_storage: OmitTableDefault<
      Static<typeof OrderItem>
    >['product_storage'];
    valuation_type: string;
    product_item_code: number;
  }
): OmitTableDefault<Static<typeof OrderItem>> {
  // Prices
  const price = Number(detail.ORIGINALPRICE);
  const latest_price = Number(detail.NET_VALUE);

  // Quantities
  const quantity = magnifyValue(
    Number(detail.ORDER_QTY),
    'stock'
  ).magnifiedValue;
  const confirmed_quantity = magnifyValue(
    Number(detail.CONFIRM_QTY),
    'stock'
  ).magnifiedValue;
  const deliveredQuantity = magnifyValue(Number(detail.DELIVERED_QTY), 'stock');
  const processedQuantity = magnifyValue(
    Number(detail.QTY_CONFIRM_FR_SAP),
    'stock'
  );
  const quantity_for_subtotal = confirmed_quantity || quantity;

  // Total
  const quantityDinero = toDinero(quantity_for_subtotal, 'stock');
  const subtotal = dineroStockMultiplyCeil(quantityDinero, latest_price);
  const processedSubtotal = dineroStockMultiplyCeil(
    processedQuantity.raw,
    latest_price
  );
  const deliveredSubtotal = dineroStockMultiplyCeil(
    deliveredQuantity.raw,
    latest_price
  );

  // Extra Conditions
  const is_not_processed = confirmed_quantity === 0;
  const is_updated = detail.ISOVERRIDEPRICE === 'X';
  const is_price_diff = latest_price !== price;
  const is_stock_diff = quantity !== confirmed_quantity;
  const price_diff = is_price_diff ? latest_price - price : 0;
  const stock_diff = is_stock_diff ? confirmed_quantity - quantity : 0;

  return {
    line_item_sap: detail.LINE_SAP,
    line_item: Number(detail.LINE_ITEM),
    quantity,
    confirmed_quantity,
    processed_quantity: processedQuantity.magnifiedValue,
    delivered_quantity: deliveredQuantity.magnifiedValue,
    received_quantity: deliveredQuantity.magnifiedValue,
    price,
    latest_price,
    original_subtotal: subtotal,
    subtotal,
    saved_subtotal: 0,
    is_not_processed,
    is_updated,
    is_price_diff,
    price_diff,
    is_stock_diff,
    stock_diff,
    order_id: rest.order_id,
    product_id: rest.product_id,
    product_storage: rest.product_storage,
    valuation_type: rest.valuation_type,
    order_type: populateOrderType(rest.product_item_code.toString()),
    processed_subtotal: processedSubtotal,
    delivered_subtotal: deliveredSubtotal,
    received_subtotal: deliveredSubtotal,
    update_origin: null,
  };
}
export function convertGroupFreeGoodDiscountPromo<
  T extends { GROUP_ID: string; PROMO: string[] }
>(data: T[]): { group_id: string; promo: string[] }[] {
  return data.map(({ GROUP_ID, PROMO }) => ({
    group_id: GROUP_ID,
    promo: PROMO,
  }));
}

export function parseSapNoShiptoList(raw?: string): number[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && /^\d+$/.test(s))
    .filter((s) => /^\d+$/.test(s))
    .map((s) => Number(s));
}
// -----------------------
// End of Transaction -> Order
// -----------------------
